import { MutableRefObject, createRef, useEffect, useRef } from 'react'
import { Box, Chip, Typography, useMediaQuery, useTheme } from '@mui/material'

import { useMap } from 'src/contexts/journey-map.context'
import { DescriptionPreview, ImageCarousel, MediaList } from 'src/components'
import { IAccount, IStep, Ilocation } from 'src/types'
import { LocationsWrapper, StepItem, StepMedia, StepsList } from './journey.styled'
import { ImpactsList } from 'src/components/journey-step-card/impacts-list.component'

interface IProps {
  steps: IStep[]
  account: IAccount
  topRef?: MutableRefObject<HTMLSpanElement | null>
}

const EMPTY_RICH_TEXT = '<p><br></p>'

export const FairtradeJourneySteps = ({ steps, account, topRef }: IProps) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { setMapCenter, setScrollRef } = useMap()

  const stepRefs = useRef<Record<string, MutableRefObject<HTMLLIElement | null>>>({})
  const registeredStepIds = useRef<Set<string>>(new Set())

  useEffect(() => {
    steps.forEach((step) => {
      if (!stepRefs.current[step.id]) {
        stepRefs.current[step.id] = createRef<HTMLLIElement>()
      }

      if (!registeredStepIds.current.has(step.id)) {
        registeredStepIds.current.add(step.id)
        setScrollRef(step.id, stepRefs.current[step.id])
      }
    })
  }, [setScrollRef, steps])

  const handleLocationClick = (location: Ilocation) => {
    if (!location.location_address || location.latitude === null || location.longitude === null) {
      return
    }

    setMapCenter(location.latitude / 10_000, location.longitude / 10_000, 14)

    if (topRef?.current) {
      if (isMobile) {
        window.scrollTo({
          top: topRef.current.offsetTop,
          behavior: 'smooth',
        })
      } else {
        topRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }
  }

  const chipColor = account.primary_color ?? theme.palette.primary.main
  const getStepMedia = (step: IStep) => {
    if (step.mainMedia && step.mainMedia.length > 0) {
      return step.mainMedia
    }
    return []
  }

  return (
    <StepsList
      id="fairtrade-journey-steps__templates-fairtrade-journey__stepslist__l67"
      sx={{
        mx: {
          xs: '6px',
          md: '0px',
        },
      }}
    >
      {steps.map((step, index) => {
        const existingRef = stepRefs.current[step.id]
        const stepRef =
          existingRef ?? (createRef<HTMLLIElement>() as MutableRefObject<HTMLLIElement | null>)

        if (!existingRef) {
          stepRefs.current[step.id] = stepRef
        }
        const stepMedia = getStepMedia(step)
        const supportingMedia = step.media ?? []
        const impacts = step.impacts ?? []
        const isOdd = index % 2 === 0
        const accountLogo = step.image
        const shouldShowAccountLogo =
          Boolean(accountLogo) &&
          step.landing_page_show &&
          step.account_approved &&
          !accountLogo.includes('assets/account-placeholder.svg')

        return (
          <StepItem
            id="fairtrade-journey-steps__templates-fairtrade-journey__stepitem__l82"
            key={step.id}
            ref={stepRef}
            isOdd={isOdd}
          >
            <Box
              id="fairtrade-journey-steps__templates-fairtrade-journey__box__header__l96"
              sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}
            >
              <Typography
                id="fairtrade-journey-steps__templates-fairtrade-journey__typography__l83"
                fontSize="40px"
                fontWeight={600}
                sx={{ flex: 1, minWidth: 0 }}
              >
                {step.name}
              </Typography>

              {shouldShowAccountLogo && (
                <Box
                  id="fairtrade-journey-steps__templates-fairtrade-journey__box__logo__l108"
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    backgroundColor: theme.palette.grey[100],
                    backgroundImage: `url(${accountLogo})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    flexShrink: 0,
                  }}
                />
              )}
            </Box>

            {step.locations?.length > 0 && (
              <LocationsWrapper id="fairtrade-journey-steps__templates-fairtrade-journey__locationswrapper__l88">
                {step.locations.map((location) => {
                  const hasCoordinates =
                    location.latitude !== null &&
                    location.longitude !== null &&
                    location.latitude !== 0 &&
                    location.longitude !== 0

                  return (
                    <Chip
                      id="fairtrade-journey-steps__templates-fairtrade-journey__chip__l97"
                      key={location.id}
                      label={location.location_name ?? 'Location'}
                      variant="outlined"
                      onClick={() => hasCoordinates && handleLocationClick(location)}
                      sx={{
                        borderColor: chipColor,
                        color: chipColor,
                        cursor: hasCoordinates ? 'pointer' : 'default',
                        opacity: hasCoordinates ? 1 : 0.6,
                      }}
                    />
                  )
                })}
              </LocationsWrapper>
            )}

            {step.description && step.description !== EMPTY_RICH_TEXT && (
              <DescriptionPreview
                richHtml={step.description}
                fontSize="20px"
                contentKey={`${step.id}-description`}
                toggleColor={chipColor}
              />
            )}

            {impacts.length > 0 && (
              <Box id="fairtrade-journey-steps__templates-fairtrade-journey__box__l119">
                <ImpactsList impacts={impacts} />
              </Box>
            )}

            {stepMedia.length > 0 ? (
              <StepMedia id="fairtrade-journey-steps__templates-fairtrade-journey__stepmedia__l125">
                <ImageCarousel images={stepMedia} isStep />
              </StepMedia>
            ) : null}

            {supportingMedia.length > 0 && (
              <>
                {/* <Divider sx={{ borderColor: 'rgba(0,0,0,0.08)' }} /> */}
                <MediaList media={supportingMedia} />
              </>
            )}
          </StepItem>
        )
      })}
    </StepsList>
  )
}
